import React, { useEffect, useState } from "react";
import { BadgePercent, Coins, Search, ShoppingBag, Ticket, TrendingUp, UserPlus, Wallet, XCircle } from "lucide-react";
import { toast } from "sonner";

import Cabecalho from "../comum/Cabecalho";
import Carregando from "../comum/Carregando";
import FiltroPeriodo from "../comum/FiltroPeriodo";
import GraficoDias from "../comum/GraficoDias";
import Kpi from "../comum/Kpi";
import SemanaHora from "../comum/SemanaHora";
import Seletor from "../comum/Seletor";
import { pct, reais } from "../comum/formato";
import { useFiltros } from "../comum/useFiltros";
import { ROTA_HUB_OFERTAS } from "../rotas";
import Alertas from "./Alertas";
import { carregarCupons } from "./api";
import ResumoFrase from "./ResumoFrase";
import TabelaCupons from "./TabelaCupons";

// Relatórios → Relatório de Ofertas → Cupons de Desconto.
export default function RelatorioCuponsPage() {
  const { filtros, mudar, query, paraApi } = useFiltros();
  const [rel, setRel] = useState(null);
  // Erro ao carregar: aviso com "Tentar de novo" no lugar do carregamento sem fim.
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const [busca, setBusca] = useState(filtros.busca || "");
  const chave = JSON.stringify(paraApi);

  useEffect(() => {
    let vivo = true;
    // Trocar período/filtro mantém o relatório na tela até o novo chegar (25/10/2026)
    setErro(false);
    carregarCupons(paraApi).then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar o relatório de cupons"); } });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, tentativa]);
  useEffect(() => {
    const t = setTimeout(() => { if ((filtros.busca || "") !== busca) mudar({ busca }); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  const r = rel?.resumo || {};
  const a = rel?.anterior || {};
  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-5" data-e2e="relatorio-cupons">
        <Cabecalho voltarPara={ROTA_HUB_OFERTAS} voltarRotulo="Relatório de Ofertas" icone={Ticket} corIcone="bg-sky-500/10 text-sky-500"
          titulo="Relatório de Cupons" subtitulo="Quando cada cupom esteve disponível, quem usou, quanto descontou e quanto vendeu." />

        <div className="flex flex-wrap items-center gap-2">
          <FiltroPeriodo de={filtros.de} ate={filtros.ate} onMudar={mudar} cor="bg-sky-500 text-white" />
          <Seletor rotulo="Módulo" valor={filtros.modulo} opcoes={rel?.opcoes?.modulos} onMudar={(v) => mudar({ modulo: v })} e2e="filtro-modulo" />
          <Seletor rotulo="Tipo" valor={filtros.tipo} opcoes={rel?.opcoes?.tipos} onMudar={(v) => mudar({ tipo: v })} e2e="filtro-tipo" />
          <Seletor rotulo="Origem" valor={filtros.categoria} opcoes={rel?.opcoes?.categorias} onMudar={(v) => mudar({ categoria: v })} />
          <Seletor rotulo="Status" valor={filtros.status} opcoes={rel?.opcoes?.status} onMudar={(v) => mudar({ status: v })} e2e="filtro-status" />
          <label className="flex items-center gap-1.5 rounded-lg border bg-card px-2.5 py-1 text-xs">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input value={busca} onChange={(e) => setBusca(e.target.value.toUpperCase())} placeholder="Código do cupom" className="bg-transparent outline-none w-32 font-mono" data-e2e="busca-cupom" />
          </label>
        </div>

        {!rel ? <Carregando erro={erro} onTentar={() => setTentativa((t) => t + 1)} /> : (
          <>
            <ResumoFrase periodo={rel.periodo} r={r} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi rotulo="Pedidos com cupom" valor={r.pedidos} atual={r.pedidos} anterior={a.pedidos} dica={`${pct(r.pct_pedidos)} dos pedidos`} icone={ShoppingBag} e2e="kpi-pedidos" />
              <Kpi rotulo="Desconto concedido" valor={reais(r.desconto)} atual={r.desconto} anterior={a.desconto} menorMelhor icone={BadgePercent} e2e="kpi-desconto" />
              <Kpi rotulo="Faturado com cupom" valor={reais(r.faturado)} atual={r.faturado} anterior={a.faturado} icone={Wallet} destaque="bg-sky-500/10 border-sky-500/30" e2e="kpi-faturado" />
              <Kpi rotulo="Ticket médio" valor={reais(r.ticket_medio)} atual={r.ticket_medio} anterior={a.ticket_medio} dica={r.ticket_sem_cupom ? `sem cupom: ${reais(r.ticket_sem_cupom)}` : null} icone={Coins} />
              <Kpi rotulo="Lucro bruto" valor={r.lucro == null ? "—" : reais(r.lucro)} atual={r.lucro} anterior={a.lucro} dica={r.lucro == null ? "sem ficha técnica" : `${pct(r.margem_pct)} margem · custo em ${pct(r.cobertura_custo_pct)}`} icone={TrendingUp} e2e="kpi-lucro" />
              <Kpi rotulo="Retorno do desconto" valor={r.retorno ? `${reais(r.retorno)}` : "—"} atual={r.retorno} anterior={a.retorno} dica="em vendas por R$ 1" icone={TrendingUp} />
              <Kpi rotulo="Clientes novos" valor={r.clientes_novos} atual={r.clientes_novos} anterior={a.clientes_novos} dica={`de ${r.clientes} clientes`} icone={UserPlus} e2e="kpi-novos" />
              <Kpi rotulo="Cancelados com cupom" valor={r.cancelados} atual={r.cancelados} anterior={a.cancelados} menorMelhor dica={r.cancelados ? reais(r.cancelado_valor) : null} icone={XCircle} />
            </div>
            <p className="text-[11px] text-muted-foreground -mt-2">
              Setas: comparação com os {rel.periodo.dias} dias anteriores. Desconto = o que realmente saiu do total do pedido. Lucro bruto = vendas dos produtos − custo (congelado na venda ou ficha técnica atual), só em pedidos com custo em todos os itens.
            </p>
            <Alertas alertas={rel.alertas} />
            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <GraficoDias serie={rel.por_dia} principal="faturado" secundaria="desconto" titulo="Vendas com cupom por dia"
                  legenda={[{ rotulo: "Faturado", cor: "bg-sky-500" }, { rotulo: "Desconto", cor: "bg-amber-400" }]} />
              </div>
              <div className="lg:col-span-2"><SemanaHora semana={rel.semana} hora={rel.hora} titulo="Quando os cupons são usados" /></div>
            </div>
            <TabelaCupons cupons={rel.cupons} query={query()} />
          </>
        )}
      </div>
    </div>
  );
}
